import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { FileUploadState, OCRFeedback, InvoiceData, ExtractionResponse } from '../types/invoice';
import OCRApiService from '../services/api';
import { getAllSubmitErrors, getAllSubmitSuccesses } from '../utils/keystoneSubmitErrors';

interface FilesContextType {
  // Upload staging area (pending files only)
  files: FileUploadState[];
  setFiles: React.Dispatch<React.SetStateAction<FileUploadState[]>>;
  addFiles: (newFiles: FileUploadState[]) => void;
  updateFile: (fileId: string, updates: Partial<FileUploadState>) => void;
  removeFile: (fileId: string) => void;
  clearAllFiles: () => void;
  // Results history (loaded from backend, separate from upload area)
  invoices: FileUploadState[];
  loadOldInvoices: () => Promise<void>;
  updateFeedback: (fileId: string, feedback: OCRFeedback) => void;
  updateEditedData: (fileId: string, editedData: InvoiceData) => void;
  updateInvoice: (fileId: string, updates: Partial<FileUploadState>) => void;
  deleteInvoice: (invoiceId: number) => Promise<void>;
  // Workspace state — used by the single-page workspace
  selectedFileId: string | null;
  setSelectedFileId: (id: string | null) => void;
  activeJobIds: number[];
  setActiveJobIds: React.Dispatch<React.SetStateAction<number[]>>;
  invoicesLoading: boolean;
  checkedIds: Set<string>;
  setCheckedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  sidebarTab: string;
  setSidebarTab: (tab: string) => void;
  formDirtyRef: React.MutableRefObject<boolean>;
  formSaveRef: React.MutableRefObject<(() => Promise<void>) | null>;
  resetAll: () => void;
}

const FilesContext = createContext<FilesContextType | undefined>(undefined);

export const useFiles = () => {
  const context = useContext(FilesContext);
  if (context === undefined) {
    throw new Error('useFiles must be used within a FilesProvider');
  }
  return context;
};

interface FilesProviderProps {
  children: ReactNode;
}

export const FilesProvider: React.FC<FilesProviderProps> = ({ children }) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [invoices, setInvoices] = useState<FileUploadState[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [activeJobIds, _setActiveJobIds] = useState<number[]>(() => {
    try {
      const saved = sessionStorage.getItem('activeJobIds');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const setActiveJobIds: React.Dispatch<React.SetStateAction<number[]>> = (value) => {
    _setActiveJobIds(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      if (next.length > 0) {
        sessionStorage.setItem('activeJobIds', JSON.stringify(next));
      } else {
        sessionStorage.removeItem('activeJobIds');
      }
      return next;
    });
  };
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [sidebarTab, setSidebarTab] = useState<string>('completed');
  const formDirtyRef = useRef(false);
  const formSaveRef = useRef<(() => Promise<void>) | null>(null);
  const recentlyDeletedIds = useRef<Set<number>>(new Set());

  // Don't restore pending files from localStorage — the actual File blobs
  // are lost on refresh, so they can't be processed. Clear stale entries.
  useEffect(() => {
    localStorage.removeItem('ocr-files');
  }, []);

  // Save files to localStorage whenever files change (excluding File objects)
  useEffect(() => {
    const serializableFiles = files.map(file => ({
      ...file,
      file: {
        name: file.file.name,
        size: file.file.size,
        type: file.file.type,
        lastModified: file.file.lastModified
      }
    }));
    localStorage.setItem('ocr-files', JSON.stringify(serializableFiles));
  }, [files]);

  const addFiles = (newFiles: FileUploadState[]) => {
    setFiles(prev => [...prev, ...newFiles]);
  };

  const updateFile = (fileId: string, updates: Partial<FileUploadState>) => {
    setFiles(prev => prev.map(file => 
      file.id === fileId ? { ...file, ...updates } : file
    ));
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const clearAllFiles = () => {
    setFiles([]);
    localStorage.removeItem('ocr-files');
  };

  const updateFeedback = (fileId: string, feedback: OCRFeedback) => {
    setInvoices(prev => prev.map(file =>
      file.id === fileId ? { ...file, feedback } : file
    ));
  };

  const updateEditedData = (fileId: string, editedData: InvoiceData) => {
    setInvoices(prev => prev.map(file =>
      file.id === fileId ? { ...file, editedData } : file
    ));
  };

  /**
   * Patch any field on an already-loaded invoice. Used by write-back flows
   * that need to mark an invoice as errored (so it moves to the Errors
   * sidebar tab with the Keystone rejection reason attached) or clear a
   * prior error after a successful retry.
   */
  const updateInvoice = (fileId: string, updates: Partial<FileUploadState>) => {
    setInvoices(prev => prev.map(file =>
      file.id === fileId ? { ...file, ...updates } : file
    ));
  };

  /**
   * Load old invoices from the backend and convert them to FileUploadState format
   */
  const loadOldInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await OCRApiService.loadOldInvoices(0, 100);

      if (!response.success || !response.invoices || response.invoices.length === 0) {
        console.log('No old invoices found');
        return;
      }

      console.log(`Found ${response.invoices.length} old invoices`);

      // Convert backend invoice format to FileUploadState
      // Using summary data for faster loading - full details can be fetched on demand
      const convertedFiles: FileUploadState[] = response.invoices.map((invoice: any) => {
        // Create a File object from the filename
        // Since we don't have the actual file, we create a placeholder File object
        const file = new File(
          [''], // Empty content since we don't have the actual file
          invoice.filename || `invoice_${invoice.id}.pdf`,
          { type: 'application/pdf' }
        );

        // Build InvoiceData from the full backend response
        const invoiceData: InvoiceData = {
          company_name: invoice.company_name || invoice.vendor_name || '',
          document_type: invoice.document_type || 'invoice',
          confidence_score: invoice.confidence_score,
          invoice_number: invoice.invoice_number,
          po_number: invoice.po_number || undefined,
          invoice_date: invoice.invoice_date,
          due_date: invoice.due_date,
          invoice_detail_line_items: invoice.invoice_detail_line_items,
          total_amount: invoice.total_amount,
          subtotal: invoice.subtotal,
          currency: invoice.currency || 'USD',
          discount: invoice.discount,
          freight: invoice.freight,
          total_tax: invoice.total_tax,
          sub_discount: invoice.sub_discount,
          sub_tax: invoice.sub_tax,
          sub_freight: invoice.sub_freight,
          state_sales_tax_maintenance_code: invoice.state_sales_tax_maintenance_code,
          notes: invoice.notes,
          terms: invoice.terms,
          file_url: invoice.file_url || invoice.fileUrl || undefined,
          vendor: invoice.vendor || (invoice.vendor_name ? {
            name: invoice.vendor_name,
            vendor_number: invoice.vendor_number,
          } : undefined),
          customer: invoice.customer || (invoice.customer_name ? {
            name: invoice.customer_name,
          } : undefined),
          line_items: invoice.line_items || [],
          tax_info: invoice.tax_info || [],
        };

        // Create ExtractionResponse
        const result: ExtractionResponse = {
          success: true,
          message: 'Loaded from database',
          data: invoiceData,
          filename: invoice.filename,
        };

        // Create FileUploadState
        const fileState: FileUploadState = {
          file,
          id: `old-invoice-${invoice.id}`,
          status: 'completed',
          progress: 100,
          result,
          invoiceId: invoice.id, // Store invoice ID for deletion
          fileSize: invoice.file_size, // Store file size
          version: invoice.version || 1, // Store version number
          isLatestVersion: invoice.is_latest_version !== false, // Store latest version flag
        };

        return fileState;
      });

      // Filter out recently deleted invoices so polling doesn't re-add them
      const filtered = recentlyDeletedIds.current.size > 0
        ? convertedFiles.filter(f => !recentlyDeletedIds.current.has(f.invoiceId!))
        : convertedFiles;

      // Merge the fresh server data with client-only state. There are two
      // sources of client-only state we need to preserve:
      //   1. In-memory `prev` — covers hot-path updates (editedData the
      //      clerk hasn't saved yet, feedback, status/error flipped during
      //      THIS browser session by a Keystone submit).
      //   2. localStorage via `getAllSubmitErrors()` — covers cold-start
      //      refreshes where `prev` is empty but we previously recorded a
      //      Keystone rejection against this invoice id. Without this, a
      //      browser refresh moves rejected invoices back to Done and the
      //      clerk loses track of what still needs attention.
      const persistedErrors = getAllSubmitErrors();
      const persistedSuccesses = getAllSubmitSuccesses();
      setInvoices(prev => {
        const prevById = new Map(prev.map(f => [f.invoiceId, f]));
        const prevIds = new Set(prev.map(f => f.invoiceId));
        const newIds = new Set(filtered.map(f => f.invoiceId));
        const sameIdSet =
          prevIds.size === newIds.size && Array.from(prevIds).every(id => newIds.has(id));

        const merged = filtered.map(fresh => {
          const existing = prevById.get(fresh.invoiceId);
          const persistedError =
            fresh.invoiceId != null ? persistedErrors[String(fresh.invoiceId)] : undefined;
          const persistedSuccess =
            fresh.invoiceId != null ? persistedSuccesses[String(fresh.invoiceId)] : undefined;

          // Precedence: in-memory `error`/`status` beats persisted (freshest),
          // persisted beats server (which has no concept of submit failures).
          const effectiveError = existing?.error ?? persistedError ?? fresh.error;
          const effectiveStatus =
            existing?.status === 'error' || (!existing && persistedError)
              ? 'error' as const
              : fresh.status;

          if (!existing && !persistedError && !persistedSuccess) return fresh;

          return {
            ...fresh,
            status: effectiveStatus,
            error: effectiveError,
            editedData: existing?.editedData ?? fresh.editedData,
            feedback: existing?.feedback ?? fresh.feedback,
            submittedAt: existing?.submittedAt ?? persistedSuccess?.submittedAt ?? fresh.submittedAt,
            submitResultKey: existing?.submitResultKey ?? persistedSuccess?.resultKey ?? fresh.submitResultKey,
            submitPdfAttached: existing?.submitPdfAttached ?? persistedSuccess?.pdfAttached ?? fresh.submitPdfAttached,
          };
        });

        // Avoid a referentially-new array when nothing meaningful changed, so
        // consumers don't re-render needlessly.
        if (sameIdSet) {
          const nothingChanged = merged.every((m, i) => {
            const p = prev[i];
            return (
              p &&
              p.invoiceId === m.invoiceId &&
              p.status === m.status &&
              p.error === m.error &&
              p.editedData === m.editedData &&
              p.feedback === m.feedback &&
              p.submittedAt === m.submittedAt &&
              p.submitResultKey === m.submitResultKey
            );
          });
          if (nothingChanged) return prev;
        }
        return merged;
      });

    } catch (error) {
      console.error('Error loading old invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  /**
   * Delete an invoice from the backend and remove it from the context
   */
  const deleteInvoice = async (invoiceId: number) => {
    // Track as recently deleted so polling doesn't re-add it
    recentlyDeletedIds.current.add(invoiceId);
    // Optimistic: remove from UI immediately
    setInvoices(prev => prev.filter(f => f.invoiceId !== invoiceId));
    try {
      await OCRApiService.deleteInvoice(invoiceId);
      // Keep in deleted set for 30 seconds to survive poll cycles
      setTimeout(() => recentlyDeletedIds.current.delete(invoiceId), 30000);
    } catch (error) {
      // Delete failed — remove from deleted set so it can come back
      recentlyDeletedIds.current.delete(invoiceId);
      console.error(`Error deleting invoice ${invoiceId}:`, error);
      throw error;
    }
  };

  const value: FilesContextType = {
    files,
    setFiles,
    addFiles,
    updateFile,
    removeFile,
    clearAllFiles,
    invoices,
    loadOldInvoices,
    updateFeedback,
    updateEditedData,
    updateInvoice,
    deleteInvoice,
    selectedFileId,
    setSelectedFileId,
    activeJobIds,
    setActiveJobIds,
    invoicesLoading,
    checkedIds,
    setCheckedIds,
    sidebarTab,
    setSidebarTab,
    formDirtyRef,
    formSaveRef,
    resetAll: () => {
      setFiles([]);
      setInvoices([]);
      setSelectedFileId(null);
      _setActiveJobIds([]);
      setCheckedIds(new Set());
      setSidebarTab('completed');
      formDirtyRef.current = false;
      formSaveRef.current = null;
      sessionStorage.removeItem('activeJobIds');
    },
  };

  return <FilesContext.Provider value={value}>{children}</FilesContext.Provider>;
};
